import { apiCandidateActions } from "@/api/candidate-action.api.ts";
import { apiCandidates } from "@/api/candidate.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { applicantsKeys } from "@/queries/recruit/use-applicants.query.ts";
import { candidatesKeys } from "@/queries/recruit/use-candidates.query.ts";
import { jobsKeys } from "@/queries/recruit/use-jobs.query.ts";
import { ApiActionCreateByFilterPayload, ApiActionPayload } from "@/types/action.types.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiCandidateMassSave } from "@/types/recruit/candidate.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const candidateActionsKeys = createQueryKeys("candidateActions", {
  list: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiCandidateActions.getAll(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useCandidateActions = (id: number, enabled: boolean = true) => {
  return useQuery({
    ...candidateActionsKeys.list(id),
    enabled: enabled,
  });
};

export const useCandidateActionCreate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: ApiActionPayload }) => {
      const result = await apiCandidateActions.create(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list(variables.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: candidatesKeys.listByApplicantId._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.details._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useCandidateActionUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      id,
      actionId,
      payload,
    }: {
      id: number;
      actionId: number;
      payload: ApiActionPayload;
    }) => {
      const result = await apiCandidateActions.update(id, actionId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list(variables.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: candidatesKeys.listByApplicantId._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.details._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

interface ActionDeleteParams {
  applicantId: number;
  candidateId: number;
  actionId: number;
}

export const useCandidateActionDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ candidateId, actionId }: ActionDeleteParams) => {
      const result = await apiCandidateActions.delete(candidateId, actionId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list(variables.candidateId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: candidatesKeys.listByApplicantId(variables.applicantId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.details._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useCandidateActionUpdateByFilter = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async (payload: ApiActionCreateByFilterPayload) => {
      const result = await apiCandidateActions.createByFilter(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list._def,
      });
      queryClient.invalidateQueries({
        queryKey: candidatesKeys.listByApplicantId._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.details._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useCandidateMassSaveByFilter = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async (payload: ApiCandidateMassSave) => {
      const result = await apiCandidates.massAction(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list._def,
      });
      queryClient.invalidateQueries({
        queryKey: candidatesKeys.listByApplicantId._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.details._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
